import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import LoadingScreen from '@/components/loading-screen';
import RetryButton from '@/components/buttons/RetryButton';
import { useAuthContext } from './useAuthContext';
import { hasPartnersAccess } from './partnersAccess';

// A ativação do parceiro escreve `is_partner` e invalida o cache do usuário no
// mesmo commit: um único refresh forçado do /user/ pega a flag nova sem exigir
// logout. Elegibilidade nunca é descoberta por 403 no overview.
export default function PartnersAccessGuard({ children }) {
  const { user, refreshUser } = useAuthContext();
  // idle → refreshing → validated (usuário veio, decide o /403) | unvalidated
  // (não deu pra buscar /user/: rede/5xx, estado recuperável, sem /403).
  const [phase, setPhase] = useState('idle');
  const attemptedRef = useRef(false);

  const allowed = hasPartnersAccess(user);

  const runRefresh = useCallback(() => {
    attemptedRef.current = true;
    setPhase('refreshing');
    Promise.resolve(refreshUser({ forceUpdate: true }))
      .then((refreshedUser) => setPhase(refreshedUser ? 'validated' : 'unvalidated'))
      .catch(() => setPhase('unvalidated'));
  }, [refreshUser]);

  useEffect(() => {
    if (allowed || attemptedRef.current) return;
    runRefresh();
  }, [allowed, runRefresh]);

  if (allowed) return children;
  if (phase === 'refreshing' || phase === 'idle') return <LoadingScreen />;

  // O /user/ respondeu e o usuário segue sem acesso → realmente não é parceiro.
  if (phase === 'validated') return <Navigate to="/403" replace />;

  // Não foi possível validar o acesso agora — oferece retry em vez de /403.
  return (
    <RetryButton
      message="Não foi possível validar seu acesso ao Cakto Partners. Tente novamente."
      onClick={runRefresh}
    />
  );
}

PartnersAccessGuard.propTypes = {
  children: PropTypes.node,
};
